import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator } from '@mui/icons-material';
import { IconButton, List, ListItem, ListItemText, Paper, Box } from '@mui/material';

interface SortableSuppliersListProps {
  suppliers: { id: number; supplierName: string }[];
  onChange: (newOrder: { id: number; supplierName: string }[]) => void;
}

const DraggableItem = ({
  id,
  supplierName,
  idx,
}: {
  idx: number;
  id: number;
  supplierName: string;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding>
      <Paper
        sx={{
          width: '100%',
          padding: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'grab',
          mb: 2
        }}
        {...attributes}
        {...listeners}
      >
        <ListItemText primary={`${idx} - ${supplierName}`} />
        <IconButton edge="end">
          <DragIndicator />
        </IconButton>
      </Paper>
    </ListItem>
  );
};

export const SortableSuppliersList = ({
  suppliers,
  onChange,
}: SortableSuppliersListProps) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = suppliers.findIndex((i) => i.id === active.id);
      const newIndex = suppliers.findIndex((i) => i.id === over.id);

      const newOrder = arrayMove(suppliers, oldIndex, newIndex);
      onChange(newOrder);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={suppliers.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <List>
          {suppliers.map((supplier, idx) => (
            <DraggableItem idx={idx + 1} key={supplier.id} {...supplier} />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
};
